import type { ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Input, SegmentedControl } from "@marekl/adminer/components";
import { PAYMENT_LABEL, type PaymentType } from "../../lib/queries/documents";
import styles from "./SettingsTab.module.css";

export interface SettingsValues {
  default_vat_rate: string;
  default_due_days: string;
  default_quote_validity_days: string;
  default_payment_type: PaymentType;
  vat_period: "monthly" | "quarterly";
}

const RATES = ["0", "12", "21"];
const DUE_DAYS = ["7", "14", "30"];

function Row({ label, description, children }: { label: string; description?: string; children: ReactNode }) {
  return (
    <div className={styles.row}>
      <div>
        <div className={styles.label}>{label}</div>
        {description && <div className={styles.description}>{description}</div>}
      </div>
      <div className={styles.control}>{children}</div>
    </div>
  );
}

const days = (v: string) => (/^\d+$/.test(v) && Number(v) <= 365) || "0–365 dní";

/**
 * "Nastavení": the defaults new documents start from, one labelled row per
 * setting (forms design: Firma). A non-VAT payer has no rate to choose.
 */
export function SettingsTab({ vatPayer }: { vatPayer: boolean }) {
  const { control, register, formState } = useFormContext<SettingsValues>();
  const e = formState.errors;
  return (
    <div className={styles.rows}>
      {vatPayer && (
        <Row label="Výchozí sazba DPH" description="Předvyplní se do nových položek.">
          <Controller
            name="default_vat_rate"
            control={control}
            render={({ field }) => (
              <SegmentedControl
                aria-label="Výchozí sazba DPH"
                options={Array.from(new Set([...RATES, field.value])).map((r) => ({ value: r, label: `${r} %` }))}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </Row>
      )}
      <Row label="Splatnost faktur" description="Počet dní od vystavení.">
        <Controller
          name="default_due_days"
          control={control}
          rules={{ validate: days }}
          render={({ field }) => (
            <div className={styles.inline}>
              <SegmentedControl
                aria-label="Obvyklá splatnost"
                options={DUE_DAYS.map((d) => ({ value: d, label: d }))}
                value={DUE_DAYS.includes(field.value) ? field.value : ""}
                onChange={field.onChange}
              />
              <Input
                aria-label="Splatnost faktur (dní)"
                inputMode="numeric"
                className={styles.days}
                value={field.value}
                onChange={(ev) => field.onChange(ev.target.value)}
                onBlur={field.onBlur}
                error={e.default_due_days?.message}
                suffix="dní"
              />
            </div>
          )}
        />
      </Row>
      <Row label="Platnost nabídek" description="Pro cenové nabídky.">
        <Input
          aria-label="Platnost nabídek (dní)"
          inputMode="numeric"
          className={styles.days}
          {...register("default_quote_validity_days", { validate: days })}
          error={e.default_quote_validity_days?.message}
          suffix="dní"
        />
      </Row>
      <Row label="Výchozí způsob platby" description="Předvyplní se do nových dokladů.">
        <Controller
          name="default_payment_type"
          control={control}
          render={({ field }) => (
            <SegmentedControl
              aria-label="Výchozí způsob platby"
              options={(Object.keys(PAYMENT_LABEL) as PaymentType[]).map((p) => ({ value: p, label: PAYMENT_LABEL[p] }))}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </Row>
    </div>
  );
}
